import * as Sentry from '@sentry/browser'

import { shouldReportError } from './filters'
import {
  sanitizeBreadcrumb,
  sanitizeEvent,
  sanitizeSessionEnvelope,
} from './privacy'

/**
 * Initialize Sentry with enhanced configuration
 * This should be called as early as possible in the app lifecycle
 */
export function initSentry(): void {
  const windowEnvironment = window._env_

  if (!windowEnvironment?.APP_SENTRY_DSN_URL) {
    console.info('Sentry DSN not configured, skipping initialization')
    return
  }

  if (Sentry.getClient()) return

  Sentry.init({
    dsn: windowEnvironment.APP_SENTRY_DSN_URL,
    transport: (options) => {
      const transport = Sentry.makeBrowserOfflineTransport(
        Sentry.makeFetchTransport,
      )(options)
      return {
        send: (envelope) => transport.send(sanitizeSessionEnvelope(envelope)),
        flush: (timeout) => transport.flush(timeout),
      }
    },
    // No production structured-log producers yet; error/message reporting stays enabled.
    enableLogs: false,
    // Keep SDK error collection/deduplication; collect explicit breadcrumbs only.
    integrations: (defaults) =>
      defaults.filter((integration) => integration.name !== 'Breadcrumbs'),
    environment: windowEnvironment.APP_ENV || 'development',
    release: windowEnvironment.BUILD_VERSION,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    profilesSampleRate: 0,
    beforeSendTransaction: () => null,
    beforeBreadcrumb: sanitizeBreadcrumb,
    beforeSend(event, hint) {
      if (!shouldReportError(event, hint)) return null
      hint.attachments = []
      return sanitizeEvent(event)
    },
    maxBreadcrumbs: 50,
  })
}

// Re-export Sentry for convenience
export * as Sentry from '@sentry/browser'

// Export context utilities
export {
  clearCustomContexts,
  setDatabaseContext,
  setOrganizationContext,
} from './context'
export {
  useManualSentryDatabaseContext,
  useSentryDatabaseContext,
} from './useSentryContext'

// Export async tracking utilities
export {
  captureMessageWithContext,
  captureWithContext,
  trackAsync,
  usePortalCallback,
  withErrorTracking,
} from './tracking'
